import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { compose, withHandlers } from 'recompose';
import ImmutablePropTypes from 'react-immutable-proptypes';
import styled from 'styled-components';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import DeleteIcon from '@material-ui/icons/Delete';
import InfoIcon from '@material-ui/icons/Info';
import CloseIcon from '@material-ui/icons/Close';
import HistoryIcon from '@material-ui/icons/History';
import ExitIcon from '@material-ui/icons/ExitToApp';
import { confirm } from '@trueprodigy/components';
import Sticky from 'react-stickynode';

import { RIGHTS } from '@constants';
import { eventDataSelector } from '@modules/events/selectors/event';
import { eventFormListSelector } from '@modules/events/selectors/eventform';
import * as eventActions from '@modules/events/actions/event';
import { userDataSelector } from '@selectors/global';
import { hasRight } from '@utils';
import { getEventForm, parseDate } from './EventDetailFormHelper';

const EventHeader = ({
  eventID,
  event,
  eventFormList,
  handleChangeEventStatus,
  hasDeleteRight,
  handleDelete,
  isOpenRH,
  onBackToSearch,
  setOpenRH,
}) => (
  <Sticky innerZ={9} top={67}>
    <EventHeader.Banner>
      <EventHeader.CloseButton aria-label="Close" onClick={onBackToSearch}>
        <CloseIcon />
      </EventHeader.CloseButton>
      <EventHeader.BannerRow>
        <Typography variant="h6">
          {eventID && (
            <React.Fragment>
              {eventID} | {getEventForm(eventFormList, event.formID)?.formName}
            </React.Fragment>
          )}
        </Typography>
        <Typography variant="h5">{eventID ? 'EDIT' : 'NEW'} EVENT</Typography>
      </EventHeader.BannerRow>
      {eventID && (
        <EventHeader.BannerRow>
          <div>
            <EventHeader.EventAttrName>Event Type:</EventHeader.EventAttrName>
            <EventHeader.EventAttrValue>
              {event.eventType}
            </EventHeader.EventAttrValue>
            <EventHeader.EventAttrName>
              {event.inactive !== 1 ? 'Active Event' : 'Inactive Event'}
            </EventHeader.EventAttrName>
            <Switch
              checked={Boolean(event.inactive !== 1)}
              color="secondary"
              onChange={ev => handleChangeEventStatus(ev.target.checked)}
            />
          </div>
          <EventHeader.Actions>
            {eventID && isOpenRH && (
              <Tooltip title="Back to Event Detail" placement="left">
                <IconButton
                  style={{ color: 'white', marginRight: '5px' }}
                  onClick={() => setOpenRH(false)}
                >
                  <ExitIcon />
                </IconButton>
              </Tooltip>
            )}
            {eventID && !isOpenRH && (
              <Tooltip title="View Revision History" placement="left">
                <IconButton
                  style={{ color: 'white', marginRight: '5px' }}
                  onClick={() => setOpenRH(true)}
                >
                  <HistoryIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip
              title={`Updated On ${parseDate(event.updateDt)} by ${
                event.updatedBy
              } | Created On ${parseDate(event.createDt)} by ${
                event.createdBy
              }`}
              placement="left"
            >
              <InfoIcon />
            </Tooltip>
            {hasDeleteRight() && (
              <IconButton aria-label="Delete" onClick={handleDelete}>
                <DeleteIcon color="error" />
              </IconButton>
            )}
          </EventHeader.Actions>
        </EventHeader.BannerRow>
      )}
    </EventHeader.Banner>
  </Sticky>
);

EventHeader.propTypes = {
  event: PropTypes.object.isRequired,
  eventFormList: ImmutablePropTypes.map,
  eventID: PropTypes.string,
  handleChangeEventStatus: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  hasDeleteRight: PropTypes.func.isRequired,
  isOpenRH: PropTypes.bool,
  setOpenRH: PropTypes.func.isRequired,
  onBackToSearch: PropTypes.func.isRequired,
};

EventHeader.Banner = styled.header`
  background: #3b5267;
  padding: 10px 40px 10px 20px;
  position: relative;
`;

EventHeader.CloseButton = styled(CloseIcon)`
  position: absolute;
  right: 3px;
  top: 3px;
  cursor: pointer;
  fill: #fff !important;
  font-size: 20px !important;
  font-weight: bold;
`;

EventHeader.BannerRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #fff;

  > h6 {
    color: #fff;
  }

  > h5 {
    color: #fff;
    font-weight: bold;
    opacity: 0.5;
  }
`;

EventHeader.EventAttrName = styled.span`
  opacity: 0.5;
`;

EventHeader.EventAttrValue = styled.span`
  margin: 0 10px 0 5px;
`;

EventHeader.Actions = styled.div`
  display: flex;
  align-items: center;

  > svg {
    cursor: pointer;
    fill: #fff;
  }

  > button {
    padding: 2px;
    margin-left: 5px;
  }
`;

const mapStateToProps = state => ({
  event: eventDataSelector(state),
  eventFormList: eventFormListSelector(state),
  userData: userDataSelector(state),
});

const mapDispatchToProps = {
  activateEvent: eventActions.activateEvent,
  deleteEvent: eventActions.deleteEvent,
  inactivateEvent: eventActions.inactivateEvent,
};

const enhance = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withHandlers({
    handleChangeEventStatus: ({
      event,
      activateEvent,
      inactivateEvent,
    }) => status => {
      if (!status) {
        inactivateEvent({
          eventID: event.eventID,
          payload: {
            lastUpdate: event.updateDt,
          },
        });
      } else {
        activateEvent({
          eventID: event.eventID,
          payload: {
            lastUpdate: event.updateDt,
          },
        });
      }
    },
    hasDeleteRight: ({ userData }) => () => {
      return hasRight(
        userData.get('userrights').toJS(),
        RIGHTS.EVENTS.DELETEEVENT,
      );
    },
    handleDelete: ({ event, deleteEvent, onBackToSearch }) => () => {
      return confirm('event')
        .then(() =>
          deleteEvent({
            eventID: event.eventID,
            payload: {
              lastUpdate: event.updateDt,
            },
          }),
        )
        .then(() => {
          onBackToSearch();
        });
    },
  }),
);

export default enhance(EventHeader);
